import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import ContentArea from '../content-area/Content';
import AnalyticsHeaderActions from './AnalyticsHeaderActions';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import AnalyticsErrorStrip from './AnalyticsErrorStrip';
import OverviewSection from './OverviewSection';
import UsersDrilldownModal from './UsersDrilldownModal';
import { useToast } from '../../context/ToastContext';
import { useAnalyticsScope } from '../../hooks/useAnalyticsScope';
import { useAdminEntities } from '../../hooks/useAdminEntities';
import { useAnalyticsSummary } from '../../hooks/useAnalyticsSummary';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { DEBOUNCE_MS, DEFAULT_FILTERS } from '../../constants/userAnalytics';
import {
  buildFilterLabels, cardValue, dateBadgeText, modalSubtext, normalizeDomain, servedRange,
} from '../../utils/analyticsMetrics';
import { buildMetricsRows, exportXlsx } from '../../utils/analyticsExport';

const isFetching = (status) => status === 'loading' || status === 'refreshing';

const visuallyHidden = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

/**
 * The whole User Analytics page. It owns the filters and the summary request; everything
 * static — header, filter bar, section frame, card frames, the popup shell — stays mounted
 * across filter changes, and only the numbers refresh.
 */
const UserAnalyticsPage = () => {
  const scope = useAnalyticsScope();
  const { entities, loading: entitiesLoading } = useAdminEntities(scope.showEntitySpaceFilter);
  const { toast } = useToast();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [modal, setModal] = useState({ open: false, card: null });
  const [liveMessage, setLiveMessage] = useState('');

  // Only typed domain text waits for the debounce; clearing the box applies straight away.
  const typedDomain = scope.showEmailDomainFilter ? normalizeDomain(filters.emailDomain) : '';
  const appliedDomain = useDebouncedValue(typedDomain, DEBOUNCE_MS, typedDomain === '');

  const { toRequestFilters } = scope;
  const requestFilters = useMemo(
    () => toRequestFilters({ ...filters, emailDomain: appliedDomain }),
    [toRequestFilters, filters, appliedDomain],
  );

  const fetchOptions = { enabled: scope.ready, resetKey: scope.resetKey };
  const summary = useAnalyticsSummary(requestFilters, fetchOptions);

  // ── Labels shared by the badge strip, the popup subtitle and the exports ──
  const range = servedRange(filters, summary.data?.filters);
  const dateBadge = dateBadgeText(filters.period, range);
  const labels = useMemo(
    () => buildFilterLabels({ filters, entities, restricted: scope.restricted, spaceName: scope.spaceName, domain: appliedDomain }),
    [filters, entities, scope.restricted, scope.spaceName, appliedDomain],
  );

  // ── Filter handlers ──
  const handleChange = useCallback((patch) => setFilters((f) => ({ ...f, ...patch })), []);
  const handleReset = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    toast.success('All filters reset to default.');
  }, [toast]);
  const toggleBreakdown = useCallback(() => setShowBreakdown((v) => !v), []);

  // ── Refresh, completion toast and the screen-reader announcement ──
  const fetching = isFetching(summary.status);
  const failed = summary.status === 'error';
  const refreshPending = useRef(false);
  const wasFetching = useRef(false);

  const { refetch: refetchSummary } = summary;
  const handleRefresh = useCallback(() => {
    if (!scope.ready) return;
    refreshPending.current = true;
    refetchSummary();
  }, [scope.ready, refetchSummary]);

  useEffect(() => {
    if (fetching) {
      wasFetching.current = true;
      setLiveMessage('');
      return;
    }
    if (!wasFetching.current) return;
    wasFetching.current = false;
    setLiveMessage('Analytics updated');
    if (refreshPending.current) {
      refreshPending.current = false;
      if (failed) toast.error("Couldn't refresh analytics.");
      else toast.success('Analytics data refreshed.');
    }
  }, [fetching, failed, toast]);

  // ── Copy / export, always from the numbers on screen ──
  const disabledReason = !summary.data
    ? (fetching ? 'Waiting for the numbers to load' : 'No data to export')
    : fetching
      ? 'Waiting for the numbers to finish updating'
      : failed
        ? 'Numbers may be stale — retry first'
        : null;

  const buildReport = () => buildMetricsRows(
    summary.data,
    { entity: labels.entity, space: labels.space, domain: labels.domain, restricted: scope.restricted },
    showBreakdown,
  );

  const handleExportXlsx = async () => {
    try {
      await exportXlsx(buildReport());
      toast.success('Formatted Excel (.xlsx) exported with auto-sized columns & alignment!');
    } catch {
      toast.error("Couldn't export the Excel file.");
    }
  };

  // ── Drill-down popup ──
  const openCard = useCallback((card) => setModal({ open: true, card }), []);
  // The card stays set while the popup animates out, so its content never refetches for "no card".
  const closeModal = useCallback(() => setModal((m) => ({ ...m, open: false })), []);
  // The pill must match the card the user clicked; a number that is still refreshing is not that.
  const cardTotal = summary.status === 'ready' ? cardValue(summary.data, modal.card) : null;

  // Every fetch above is gated on scope.ready, so nothing has been requested for a user without access.
  if (!scope.ready) return <Navigate to="/unauthorized" replace />;

  return (
    <ContentArea
      title="User Analytics"
      subtitle="Simplified usage and active user metrics"
      icon={<LeaderboardOutlinedIcon />}
      titleAction={(
        <AnalyticsHeaderActions
          onExportXlsx={handleExportXlsx}
          onRefresh={handleRefresh}
          refreshing={fetching}
          disabledReason={disabledReason}
        />
      )}
    >
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 3 } }}>
        <AnalyticsFilterBar
          filters={filters}
          onChange={handleChange}
          onReset={handleReset}
          showEntitySpaceFilter={scope.showEntitySpaceFilter}
          showEmailDomainFilter={scope.showEmailDomainFilter}
          restricted={scope.restricted}
          entities={entities}
          entitiesLoading={entitiesLoading}
          labels={labels}
          dateBadge={dateBadge}
        />

        <AnalyticsErrorStrip error={summary.error} hasData={Boolean(summary.data)} what="analytics" onRetry={summary.refetch} />

        <OverviewSection summary={summary} showBreakdown={showBreakdown} onToggleBreakdown={toggleBreakdown} onOpenCard={openCard} />
      </Box>

      <UsersDrilldownModal
        open={modal.open}
        card={modal.card}
        onClose={closeModal}
        requestFilters={requestFilters}
        resetKey={scope.resetKey}
        cardTotal={cardTotal}
        subtitle={modalSubtext(labels, dateBadge, { restricted: scope.restricted })}
        cmsTracked={summary.data?.capabilities?.cmsActivityTracked === true}
      />

      <Box role="status" aria-live="polite" sx={visuallyHidden}>{liveMessage}</Box>
    </ContentArea>
  );
};

export default UserAnalyticsPage;
